import { useEffect, useMemo, useState } from 'react';
import { Card } from 'vopay';
import { Prompt } from 'react-router-dom';
import { Title, UtilButton, Skeleton } from 'UI';
import { useTranslation } from 'react-i18next';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import type { UserRoles } from 'vopay';
import ApproversDetails from './ApproversDetails';
import ApproversEdit from './ApproversEdit';
import styles from './Approvers.module.scss';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import { usePermissions } from '@/providers/Permissions.provider';
import useFetchApproverLinkedAccounts from '@/hooks/useApi/useFetchApproverLinkedAccounts';
import { useClientData } from '@/providers/ClientData.provider';
interface ApproversProps {
  isDisabled?: boolean;
  onEdit: (isEditing: boolean) => void;
}

const Approvers = ({ isDisabled = false, onEdit }: ApproversProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Roles.Approvers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { response, fetchUserRoles } = useFetchUserRoles();
  const [isEditing, setIsEditing] = useState(false);
  const [autoStartedEdit, setAutoStartedEdit] = useState(false);
  const [, setLoading] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const { workflows, fetchWorkflows, fetchingWorkflows } = useFetchWorkflows();
  const { permissions } = usePermissions();
  const { fetchApproverLinkedAccounts } = useFetchApproverLinkedAccounts();
  const {
    response: { subAccountPermissions },
  } = useClientData();
  const [linkedAccountsMap, setLinkedAccountsMap] = useState<Record<string, string[]>>({});

  const fetchLinkedAccounts = async (usernames: string[]) => {
    if (!subAccountPermissions || usernames.length === 0) return;
    const results = await Promise.allSettled(
      usernames.map((username) =>
        fetchApproverLinkedAccounts({ Username: username, TransactionApprovalRole: 'approver' }),
      ),
    );
    const map: Record<string, string[]> = {};
    usernames.forEach((username, i) => {
      const result = results[i];
      map[username] =
        result.status === 'fulfilled' && result.value ? result.value.Accounts.map((a) => a.APIAccountID) : [];
    });
    setLinkedAccountsMap(map);
  };

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        setLoading(true);
        const rolesRes = await fetchUserRoles({ Role: 'approver' });
        const usernames = [...new Set((rolesRes?.UserRolePermissions ?? []).map((u) => u.Username))];
        await fetchLinkedAccounts(usernames);
        if ((rolesRes?.UserRolePermissions ?? []).length === 0) {
          setIsEditing(true);
          setAutoStartedEdit(true);
          onEdit(true);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('loadErrorMessage', { defaultValue: 'Failed to load approvers. Please try again.' });
        await alertDialog.error(t('loadErrorTitle', { defaultValue: 'Error' }), errorMessage, {
          abortLabel: t('closeButton', { defaultValue: 'Close' }),
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserRoles();
    refreshWorkflows();
  }, []);

  const approvers = response?.UserRolePermissions || [];

  const activeTransactionTypeIDs = useMemo(() => {
    return workflows.filter((group) => group.Workflows.some((wf) => wf.IsActive)).map((group) => group.TransactionType);
  }, [workflows]);

  // Group approvers by username
  const groupedApprovers = useMemo(() => {
    if (!response?.UserRolePermissions) return {};

    const grouped: Record<string, UserRoles[]> = {};

    response.UserRolePermissions.forEach((userRole) => {
      if (!grouped[userRole.Username]) {
        grouped[userRole.Username] = [];
      }
      grouped[userRole.Username].push(userRole);
    });

    return grouped;
  }, [response?.UserRolePermissions]);

  const handleEdit = () => {
    setIsEditing(true);
    onEdit(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    onEdit(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const rolesRes = await fetchUserRoles({ Role: 'approver' });
      const usernames = [...new Set((rolesRes?.UserRolePermissions ?? []).map((u) => u.Username))];
      await fetchLinkedAccounts(usernames);
      setIsEditing(false);
      setAutoStartedEdit(false);
      onEdit(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('refreshErrorMessage', { defaultValue: 'Failed to refresh approvers. Please reload the page.' });
      await alertDialog.error(t('refreshErrorTitle', { defaultValue: 'Error' }), errorMessage, {
        abortLabel: t('closeButton', { defaultValue: 'Close' }),
      });
    } finally {
      setLoading(false);
    }
  };

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch (err) {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }

  return (
    <Card className={styles.card} style={{ opacity: isDisabled ? 0.5 : 1 }}>
      <div className={styles.header}>
        <Title level="4">{t('title', { defaultValue: 'Approvers' })}</Title>

        {fetchingWorkflows ? (
          <div className={styles.editSkeleton}>
            <Skeleton height="100%" />
          </div>
        ) : (
          !isEditing &&
          permissions.ManageUserPermissions && (
            <UtilButton onClick={handleEdit} iconLeft={approvers.length === 0 ? 'add' : 'edit'} disabled={isDisabled}>
              {approvers.length === 0
                ? t('addButton', { defaultValue: 'Add Approver' })
                : t('editButton', { defaultValue: 'Edit Approvers' })}
            </UtilButton>
          )
        )}
      </div>

      {isEditing ? (
        <ApproversEdit
          approvers={approvers}
          activeTransactionTypeIDs={activeTransactionTypeIDs}
          linkedAccountsMap={linkedAccountsMap}
          hideCancel={autoStartedEdit}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ) : (
        <ApproversDetails approvers={groupedApprovers} linkedAccountsMap={linkedAccountsMap} />
      )}
      <Prompt
        when={isEditing}
        message={t('discardPromptMessage', { defaultValue: 'The approver details you entered will not be saved.' })}
      />
    </Card>
  );
};

export default Approvers;
